/**
 * Form Data Transformers for Step 5 (Post Assessment)
 * 
 * Handles transformation between form state, store data, and AI responses
 * Adapted for React Hook Form (JobPostFormData)
 */

import { JobPostFormData } from '../../../schemas/jobPostValidation';
import { JobDescriptionEntity, Interviewer } from '../../../../../Entities/JobDescription';

/**
 * Local state type for Post Assessment (used in component)
 */
export interface PostAssessmentState {
  interviewMode: 'online' | 'in-person';
  interviewers: Interviewer[];
  interviewGuests: string[];
}

/**
 * Transform local state to Step5Data (form format)
 */
export const transformFormToStep5Data = (state: PostAssessmentState): JobPostFormData['step5'] => {
  // Map interview mode to onlineInterview boolean
  // 'online' maps to true, 'in-person' maps to false
  const onlineInterview = state.interviewMode === 'online';
  
  return {
    onlineInterview,
    interviewers: state.interviewers || [],
    interviewGuests: state.interviewGuests || [],
  };
};

/**
 * Transform Step5Data to local state (for component)
 */
export const transformStoreToStep5 = (step5Data: JobPostFormData['step5'] | null | undefined): PostAssessmentState => {
  if (!step5Data) {
    return {
      interviewMode: 'in-person', // Default to Team Will Contact
      interviewers: [],
      interviewGuests: [],
    };
  }

  // Determine interview mode from onlineInterview boolean
  // If onlineInterview is true, use 'online', otherwise 'in-person'
  let interviewMode: 'online' | 'in-person' = step5Data.onlineInterview ? 'online' : 'in-person';

  return {
    interviewMode,
    // Ensure interviewers have email field and correct referenceType
    interviewers: (step5Data.interviewers || []).map(interviewer => ({
      ...interviewer,
      email: interviewer.email || '', // Ensure email is always a string
      referenceType: (interviewer.referenceType || 'User') as 'User', // Ensure referenceType is "User"
    })),
    interviewGuests: step5Data.interviewGuests || [],
  };
};

/**
 * Map JobDescriptionEntity to Step5Data (for edit/clone scenarios)
 */
export const mapJobDescriptionToStep5 = (jobPost: JobDescriptionEntity): JobPostFormData['step5'] | null => {
  return {
    onlineInterview: jobPost.onlineInterview ?? false,
    interviewers: jobPost.interviewers || [],
    interviewGuests: jobPost.interviewGuests || [],
  };
};

/**
 * Map AI response to Step5Data format
 */
export const mapAIResponseToStep5Data = (aiResponse: any): JobPostFormData['step5'] => {
  // AI response structure may vary - adapt based on actual API response
  return {
    onlineInterview: aiResponse?.onlineInterview ?? false,
    interviewers: aiResponse?.interviewers || [],
    interviewGuests: aiResponse?.interviewGuests || [],
  };
};

/**
 * Merge AI-generated data with existing user data
 */
export const mergeAIWithUserData = (
  existingData: JobPostFormData['step5'] | null,
  aiData: JobPostFormData['step5']
): JobPostFormData['step5'] => {
  // For Post Assessment, we'll merge interviewers and guests but preserve user's mode preference
  return {
    ...existingData,
    ...aiData,
    // Preserve existing interviewers and guests unless AI provides new ones
    interviewers: (aiData.interviewers && aiData.interviewers.length > 0) ? aiData.interviewers : (existingData?.interviewers || []),
    interviewGuests: (aiData.interviewGuests && aiData.interviewGuests.length > 0) ? aiData.interviewGuests : (existingData?.interviewGuests || []),
  };
};


